import { useCallback, useState } from 'react';
import fetchJson from '@atlassian/jira-fetch/src/utils/as-json.tsx';
import {
	type CreateOrEditPriorityScheme,
	type ProjectInput,
	type RemoteDataForProjectPicker,
	ProjectPickerState,
	ProjectFilterChoice,
} from '../../common/ui/types';
import {
	fetchAllProjects,
	fetchPrioritySchemeProjectSuggestions,
	fetchProjectsAssociatedToPriorityScheme,
} from './get-projects';

type OnRefetchParams = {
	projectFilter: (typeof ProjectFilterChoice)[keyof typeof ProjectFilterChoice];
	query: string;
	startAt: number;
};

export type ProjectPickerServiceReturn = {
	data: RemoteDataForProjectPicker;
	state: (typeof ProjectPickerState)[keyof typeof ProjectPickerState];
	onRefetch: (params: OnRefetchParams) => void;
};

export const initData = {
	countForQueriedProjectsInJira: 0,
	countForQueriedAssociatedProjects: 0,
	totalNumberOfAssociatedProjects: 0,
	priorityScheme: {
		name: '',
		isDefault: false,
	},
	projects: [],
};

export const useProjectPickerService = (
	priorityScheme: CreateOrEditPriorityScheme,
): ProjectPickerServiceReturn => {
	const [data, setData] = useState<RemoteDataForProjectPicker>({
		...initData,
		priorityScheme: {
			name: priorityScheme.name ?? '',
			isDefault: priorityScheme.isDefault,
		},
	});
	const [state, setState] = useState<(typeof ProjectPickerState)[keyof typeof ProjectPickerState]>(
		ProjectPickerState.INITITAL,
	);

	const onRefetch = useCallback(
		async ({ projectFilter, query, startAt }: OnRefetchParams) => {
			const prevErrorState = ProjectPickerState.ERROR === state;
			const prevInitialState = ProjectPickerState.INITITAL === state;

			setState(ProjectPickerState.LOADING);

			const {
				countForQueriedProjectsInJira,
				countForQueriedAssociatedProjects,
				totalNumberOfAssociatedProjects,
				priorityScheme: { name, isDefault },
			} = data;

			try {
				let projects: ProjectInput[] = [];
				let countForProjectsInJira: number = countForQueriedProjectsInJira;
				let countForAssociatedProjects: number = countForQueriedAssociatedProjects;
				let totalNumOfAssociatedProjects: number = totalNumberOfAssociatedProjects;

				if (!priorityScheme.id) {
					const { items, totalItems } = await fetchAllProjects(fetchJson, startAt, query);
					countForProjectsInJira = totalItems;
					projects = items;
				} else if (projectFilter === ProjectFilterChoice.ALL_PROJECTS) {
					// Populate value for lozenge used in "Existing projects" filter dropdown
					if (prevErrorState || prevInitialState) {
						const { totalItems } = await fetchProjectsAssociatedToPriorityScheme(
							fetchJson,
							priorityScheme.id,
							0,
							'',
						);
						totalNumOfAssociatedProjects = totalItems;
					}

					const { items, totalItems } = await fetchPrioritySchemeProjectSuggestions(
						fetchJson,
						priorityScheme.id,
						startAt,
						query,
					);

					countForProjectsInJira = totalItems;
					projects = items;
				} else {
					const { items, totalItems } = await fetchProjectsAssociatedToPriorityScheme(
						fetchJson,
						priorityScheme.id,
						startAt,
						query,
					);

					countForAssociatedProjects = totalItems;
					projects = items;
				}

				const nextData = {
					countForQueriedProjectsInJira: countForProjectsInJira,
					countForQueriedAssociatedProjects: countForAssociatedProjects,
					totalNumberOfAssociatedProjects: totalNumOfAssociatedProjects,
					priorityScheme: {
						name,
						isDefault,
					},
					projects,
				};

				setData(nextData);
				setState(ProjectPickerState.SUCCESS);
			} catch (error) {
				setState(ProjectPickerState.ERROR);
			}
		},
		[data, priorityScheme.id, state],
	);

	return {
		data,
		onRefetch,
		state,
	};
};
